import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Upload,
  TreeSelect,
  Radio,
  Space,
  message,
  Row,
  Col,
  Divider,
  Typography,
  Alert,
  Breadcrumb,
  Modal,
  Image
} from 'antd';
import {
  SaveOutlined,
  UploadOutlined,
  FileTextOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  LinkOutlined,
  AppstoreOutlined,
  DeleteOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';
import { useFixedFooter, useFixedFooterPlaceholder } from '../hooks/useFixedFooter';
import MaterialSelector from '../components/MaterialSelector';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const MaterialEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // 如果有id说明是编辑模式
  const [searchParams] = useSearchParams();
  const [materialType, setMaterialType] = useState(searchParams.get('type') || 'TEXT'); // 从URL参数获取类型
  
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [groupTree, setGroupTree] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [coverFileList, setCoverFileList] = useState([]);
  const [previewUrls, setPreviewUrls] = useState({}); // 存储转换后的预览URL
  const [currentMaterial, setCurrentMaterial] = useState(null); // 存储当前素材数据

  // 封面素材选择器状态
  const [coverSelectorVisible, setCoverSelectorVisible] = useState(false);
  const [selectedCoverMaterial, setSelectedCoverMaterial] = useState(null);
  const [previewModal, setPreviewModal] = useState({ open: false, src: '', type: 'image', title: '' });
  
  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();
  
  // 校验小程序标题字符长度（最多20个字）
  const validateMiniprogramTitle = async (_, value) => {
    const val = (value || '').trim();
    if (!val) {
      return Promise.reject('请输入小程序消息标题');
    }
    if (val.length > 20) {
      return Promise.reject('标题最多20个字');
    }
    return Promise.resolve();
  };

  // 获取租户ID
  const getTenantId = () => {
    try {
      const tenantStr = localStorage.getItem('tenant');
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        return tenant.id;
      }
    } catch (error) {
      console.error('获取租户ID失败:', error);
    }
    return null;
  };
  
  const tenantId = getTenantId();
  const isEdit = !!id;

  // 获取OSS预览URL
  const getOssPreviewUrl = async (ossUrl) => {
    if (!ossUrl) return null;
    
    // 如果已经是HTTP URL，直接返回
    if (ossUrl.startsWith('http')) {
      return ossUrl;
    }
    
    // 如果是OSS协议URL，请求转换
    if (ossUrl.startsWith('oss://')) {
      try {
        const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(ossUrl)}`);
        const data = await response.json();
        
        if (data.success && data.url) {
          return data.url;
        }
      } catch (error) {
        console.error('获取预览URL失败:', error);
      }
    }
    
    return ossUrl; // 后备方案，返回原URL
  };
  
  // 通过mediaId获取媒体信息
  const fetchMediaInfo = async (mediaId) => {
    if (!mediaId || !tenantId) return;
    
    try {
      console.log('尝试获取媒体信息，mediaId:', mediaId, 'tenantId:', tenantId);
      
      // 尝试刷新媒体信息并获取OSS URL
      const response = await api.post('/media/wework/refresh', {
        mediaId: mediaId,
        tenantId: tenantId
      });
      
      console.log('媒体信息刷新响应:', response.data);
      
      if (response.data.success && response.data.ossUrl) {
        const ossUrl = response.data.ossUrl;
        console.log('获取到OSS URL:', ossUrl);
        
        // 获取预览URL
        const previewUrl = await getOssPreviewUrl(ossUrl);
        console.log('小程序封面图预览URL:', previewUrl);
        
        if (previewUrl) {
          setPreviewUrls(prev => ({
            ...prev,
            miniprogramCover: previewUrl
          }));
          setCoverFileList([{
            uid: '-1',
            name: '小程序封面',
            status: 'done',
            url: previewUrl,
            originalUrl: ossUrl,
            mediaId: mediaId
          }]);
        }
      } else {
        console.log('获取媒体信息失败:', response.data.message);
      }
    } catch (error) {
      console.error('获取媒体信息异常:', error);
    }
  };

  // 素材类型配置
  const materialTypes = [
    { value: 'TEXT', label: '文字', icon: <FileTextOutlined />, color: 'blue' },
    { value: 'IMAGE', label: '图片', icon: <FileImageOutlined />, color: 'green' },
    { value: 'VIDEO', label: '视频', icon: <VideoCameraOutlined />, color: 'purple' },
    { value: 'LINK', label: '链接', icon: <LinkOutlined />, color: 'orange' },
    { value: 'MINIPROGRAM', label: '小程序', icon: <AppstoreOutlined />, color: 'red' }
  ];

  const currentTypeConfig = materialTypes.find(t => t.value === materialType);

  // 加载分组树
  const loadGroupTree = async () => {
    if (!tenantId) {
      message.error('未找到租户信息，请重新登录');
      return;
    }
    
    try {
      const response = await api.get('/material-groups/tree', {
        params: { tenantId }
      });
      if (response.data.success) {
        setGroupTree(convertToTreeSelectData(response.data.data));
      }
    } catch (error) {
      message.error('加载分组失败');
    }
  };

  // 转换分组数据为TreeSelect格式
  const convertToTreeSelectData = (groups) => {
    if (!groups || groups.length === 0) return [];
    
    // 过滤掉虚拟节点（"全部"和"未分组"）
    const realGroups = groups.filter(group => 
      group && group.id !== null && group.id !== -1 && typeof group.id === 'number'
    );
    
    return realGroups.map(group => ({
      value: group.id,
      title: group.name,
      children: group.children ? convertToTreeSelectData(group.children) : []
    }));
  };

  // 加载素材详情（编辑模式）
  const loadMaterialDetail = async () => {
    if (!isEdit) return;
    
    if (!tenantId) {
      message.error('未找到租户信息，请重新登录');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`/materials/${id}`);
      if (response.data.success) {
        const material = response.data.data;
        setCurrentMaterial(material); // 保存当前素材数据
        
        // 设置素材类型（编辑模式下从数据中获取）
        if (material.materialType) {
          setMaterialType(material.materialType);
        }
        
        // 设置表单基础信息
        form.setFieldsValue({
          name: material.name,
          groupId: material.groupId,
          description: material.description,
          tags: material.tags
        });

        // 根据类型设置特定字段
        switch (material.materialType) {
          case 'TEXT':
            form.setFieldsValue({
              textContent: material.textContent
            });
            break;
          case 'IMAGE':
            if (material.imageInfo) {
              form.setFieldsValue({
                altText: material.imageInfo.altText
              });
              // 如果有现有图片，显示在文件列表中
              if (material.imageInfo.url) {
                // 处理OSS URL转换
                getOssPreviewUrl(material.imageInfo.url).then(previewUrl => {
                  setFileList([{
                    uid: '-1',
                    name: material.imageInfo.fileName || '图片',
                    status: 'done',
                    url: previewUrl,
                    originalUrl: material.imageInfo.url // 保存原始URL
                  }]);
                });
              }
            }
            break;
          case 'VIDEO':
            if (material.videoInfo) {
              console.log('视频数据:', material.videoInfo);
              
              form.setFieldsValue({
                videoTitle: material.videoInfo.title,
                videoDescription: material.videoInfo.description
              });
              if (material.videoInfo.url) {
                // 处理OSS URL转换
                getOssPreviewUrl(material.videoInfo.url).then(previewUrl => {
                  console.log('视频预览URL:', previewUrl);
                  setFileList([{
                    uid: '-1',
                    name: material.videoInfo.title || '视频',
                    status: 'done',
                    url: previewUrl,
                    originalUrl: material.videoInfo.url, // 保存原始URL
                    type: 'video'
                  }]);
                });
              }
            }
            break;
          case 'LINK':
            if (material.linkInfo) {
              form.setFieldsValue({
                linkTitle: material.linkInfo.title,
                linkUrl: material.linkInfo.url,
                linkDescription: material.linkInfo.description,
                linkPicUrl: material.linkInfo.picUrl
              });
              
              // 处理链接封面图片预览
              if (material.linkInfo.picUrl) {
                getOssPreviewUrl(material.linkInfo.picUrl).then(previewUrl => {
                  setPreviewUrls(prev => ({
                    ...prev,
                    linkPreview: previewUrl
                  }));
                });
              }
            }
            break;
          case 'MINIPROGRAM':
            if (material.miniprogramInfo) {
              // 调试：打印小程序信息
              console.log('小程序数据:', material.miniprogramInfo);
              
              form.setFieldsValue({
                miniprogramTitle: material.miniprogramInfo.title,
                miniprogramAppid: material.miniprogramInfo.appid,
                miniprogramPage: material.miniprogramInfo.page,
                miniprogramDescription: material.miniprogramInfo.description
              });
              
              // 处理小程序封面图片
              const picMediaId = material.miniprogramInfo.picMediaId;
              const picUrl = material.miniprogramInfo.picUrl || material.miniprogramInfo.coverUrl;
              
              console.log('小程序封面图 - picMediaId:', picMediaId, ', picUrl:', picUrl);
              
              if (picUrl) {
                // 如果有直接的图片URL，使用它
                console.log('使用直接图片URL:', picUrl);
                getOssPreviewUrl(picUrl).then(previewUrl => {
                  console.log('小程序封面图预览URL:', previewUrl);
                  setPreviewUrls(prev => ({
                    ...prev,
                    miniprogramCover: previewUrl
                  }));
                  setCoverFileList([{
                    uid: '-1',
                    name: '小程序封面',
                    status: 'done',
                    url: previewUrl,
                    originalUrl: picUrl
                  }]);
                });
              } else if (picMediaId) {
                // 如果只有mediaId，需要通过API获取媒体信息
                console.log('通过mediaId获取封面图:', picMediaId);
                fetchMediaInfo(picMediaId);
              } else {
                console.log('未找到小程序封面图片信息');
              }
            }
            break;
        }
      }
    } catch (error) {
      message.error('加载素材详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理URL参数中的groupId预选
  useEffect(() => {
    const groupIdParam = searchParams.get('groupId');
    if (groupIdParam && !isEdit) {
      // 只在创建模式下预选分组
      const groupId = parseInt(groupIdParam);
      if (!isNaN(groupId)) {
        form.setFieldsValue({ groupId });
      }
    }
  }, [searchParams, isEdit, form]);

  // 初始化
  useEffect(() => {
    loadGroupTree();
    loadMaterialDetail();
  }, [id]);

  // 清理预览URL，避免内存泄漏
  useEffect(() => {
    return () => {
      // 清理通过 URL.createObjectURL 创建的本地预览URL
      coverFileList.forEach(file => {
        if (file.url && file.url.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
        if (file.thumbUrl && file.thumbUrl.startsWith('blob:')) {
          URL.revokeObjectURL(file.thumbUrl);
        }
      });
      
      fileList.forEach(file => {
        if (file.url && file.url.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
        if (file.thumbUrl && file.thumbUrl.startsWith('blob:')) {
          URL.revokeObjectURL(file.thumbUrl);
        }
      });
    };
  }, [coverFileList, fileList]);

  // 文件上传配置
  const uploadProps = {
    beforeUpload: (file) => {
      // 检查文件大小（10MB限制）
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        let errorMsg = '文件大小超过限制';
        if (materialType === 'IMAGE') {
          errorMsg = '图片大小不能超过10MB，请压缩后重试';
        } else if (materialType === 'VIDEO') {
          errorMsg = '视频大小不能超过10MB，请压缩后重试';
        }
        message.error(errorMsg);
        return Upload.LIST_IGNORE;
      }
      
      // 不自动上传，由表单提交时处理
      return false;
    },
    onRemove: (file) => {
      // 移除指定文件
      const newFileList = fileList.filter(item => item.uid !== file.uid);
      setFileList(newFileList);
    },
    fileList,
    onChange: ({ fileList: newFileList }) => {
      // 为新上传的文件生成预览URL
      const processedFileList = newFileList.map(file => {
        if (file.originFileObj && !file.url && !file.thumbUrl) {
          // 为新上传的文件生成本地预览URL
          const localUrl = URL.createObjectURL(file.originFileObj);
          return {
            ...file,
            url: localUrl,
            thumbUrl: localUrl
          };
        }
        return file;
      });
      
      setFileList(processedFileList);
    },
    onPreview: async (file) => {
      const src = file.url || file.thumbUrl;
      if (!src) return;
      const isVideo = file.type === 'video' || (file.originFileObj && file.originFileObj.type?.startsWith('video/')) || /\.(mp4|mov|webm|m4v)$/i.test(file.name || '');
      setPreviewModal({ open: true, src, type: isVideo ? 'video' : 'image', title: file.name || (isVideo ? '视频预览' : '图片预览') });
    }
  };

  // 封面图片上传配置（小程序用）
  const coverUploadProps = {
    beforeUpload: (file) => {
      // 检查文件大小（10MB限制）
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        message.error('小程序封面图片大小不能超过10MB，请压缩后重试');
        return Upload.LIST_IGNORE;
      }
      
      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        message.error('只支持图片格式的封面');
        return Upload.LIST_IGNORE;
      }
      
      return false;
    },
    onRemove: () => {
      setCoverFileList([]);
      // 清理预览URL
      setPreviewUrls(prev => ({
        ...prev,
        miniprogramCover: null
      }));
    },
    fileList: coverFileList,
    onChange: ({ fileList: newFileList }) => {
      // 为新上传的文件生成预览URL
      const processedFileList = newFileList.map(file => {
        if (file.originFileObj && !file.url && !file.thumbUrl) {
          // 为新上传的文件生成本地预览URL
          const localUrl = URL.createObjectURL(file.originFileObj);
          return {
            ...file,
            url: localUrl,
            thumbUrl: localUrl
          };
        }
        return file;
      });
      
      setCoverFileList(processedFileList);
      
      // 同时更新预览URLs状态
      if (processedFileList.length > 0 && processedFileList[0].url) {
        setPreviewUrls(prev => ({
          ...prev,
          miniprogramCover: processedFileList[0].url
        }));
      } else {
        setPreviewUrls(prev => ({
          ...prev,
          miniprogramCover: null
        }));
      }
    },
    onPreview: async (file) => {
      const src = file.url || file.thumbUrl;
      if (!src) return;
      setPreviewModal({ open: true, src, type: 'image', title: file.name || '封面预览' });
    }
  };

  // 处理链接封面素材选择
  const handleSelectCoverMaterial = async (material) => {
    try {
      console.log('选择封面素材:', material);

      // 调用后端API获取适配消息场景的格式
      const response = await api.get(`/materials/${material.id}/wework-format/message`);

      if (response.data.success) {
        const weworkData = response.data.data;
        console.log('获取企微格式成功:', weworkData);

        // 设置表单字段值
        form.setFieldsValue({
          linkPicUrl: weworkData.image?.url || material.content?.image?.url,
          linkPicMediaId: weworkData.image?.mediaId
        });

        setSelectedCoverMaterial(material);

        // 更新预览
        setPreviewUrls(prev => ({
          ...prev,
          linkPreview: weworkData.image?.url || material.content?.image?.url
        }));

        setCoverSelectorVisible(false);
        message.success('已设置封面图片');
      } else {
        message.error('获取素材数据失败');
      }
    } catch (error) {
      console.error('设置封面失败:', error);
      message.error('设置封面失败，请重试');
    }
  };

  // 提交表单
  const handleSubmit = async (values) => {
    if (!tenantId) {
      message.error('未找到租户信息，请重新登录');
      return;
    }
    
    setLoading(true);
    
    try {
      let response;
      const submitData = {
        ...values,
        tenantId,
        materialType
      };

      // 根据素材类型构建不同的请求
      switch (materialType) {
        case 'TEXT':
          submitData.textContent = values.textContent;
          if (isEdit) {
            response = await api.put(`/materials/${id}`, submitData);
          } else {
            response = await api.post('/materials/text', submitData);
          }
          break;

        case 'IMAGE':
          if (!isEdit && fileList.length === 0) {
            message.error('请选择图片文件');
            setLoading(false);
            return;
          }
          
          if (isEdit) {
            // 编辑模式：保持原有逻辑，单张更新
            const imageFormData = new FormData();
            imageFormData.append('material', new Blob([JSON.stringify(submitData)], {type: 'application/json'}));
            
            if (fileList.length > 0 && fileList[0].originFileObj) {
              imageFormData.append('file', fileList[0].originFileObj);
            }
            
            console.log('编辑图片素材，是否有新文件:', fileList.length > 0 && fileList[0].originFileObj);
            response = await api.put(`/materials/${id}/image`, imageFormData, {
              headers: {
                'Content-Type': undefined
              }
            });
          } else {
            // 创建模式：支持批量创建
            if (fileList.length === 0 || !fileList.some(file => file.originFileObj)) {
              message.error('请选择图片文件');
              setLoading(false);
              return;
            }
            
            // 创建模式：统一使用批量创建接口，无论单张还是多张
            const batchFormData = new FormData();
            
            // 添加所有文件
            fileList.forEach(file => {
              if (file.originFileObj) {
                batchFormData.append('files', file.originFileObj);
              }
            });
            
            // 添加其他参数
            batchFormData.append('tenantId', tenantId);
            if (values.groupId) {
              batchFormData.append('groupId', values.groupId);
            }
            if (values.description) {
              batchFormData.append('description', values.description);
            }
            
            response = await api.post('/materials/image/batch', batchFormData, {
              headers: {
                'Content-Type': undefined
              }
            });
            
            // 处理批量创建结果
            if (response.data.success) {
              const { successCount, failCount, results } = response.data.data;
              
              if (failCount === 0) {
                message.success(`成功创建 ${successCount} 个图片素材`);
              } else {
                message.warning(`成功创建 ${successCount} 个素材，${failCount} 个失败`);
                
                // 显示失败详情
                results.filter(result => !result.success).forEach(result => {
                  message.error(`${result.fileName}: ${result.error}`, 3);
                });
              }
              
              // 跳转回素材列表
              navigate('/materials');
              return;
            } else {
              message.error(response.data.message || '批量创建失败');
              setLoading(false);
              return;
            }
          }
          break;

        case 'VIDEO':
          if (!isEdit && fileList.length === 0) {
            message.error('请选择视频文件');
            setLoading(false);
            return;
          }
          
          const videoFormData = new FormData();
          // 后端期望的参数名是'material'和'file'
          videoFormData.append('material', new Blob([JSON.stringify(submitData)], {type: 'application/json'}));
          if (fileList.length > 0 && fileList[0].originFileObj) {
            videoFormData.append('file', fileList[0].originFileObj);
          }
          
          if (isEdit) {
            // 编辑模式：使用支持文件上传的更新接口
            response = await api.put(`/materials/${id}/video`, videoFormData, {
              headers: {
                'Content-Type': undefined
              }
            });
          } else {
            // 删除默认的Content-Type，让浏览器自动设置multipart/form-data
            response = await api.post('/materials/video', videoFormData, {
              headers: {
                'Content-Type': undefined
              }
            });
          }
          break;

        case 'LINK':
          submitData.linkInfo = {
            title: values.linkTitle,
            url: values.linkUrl,
            description: values.linkDescription,
            picUrl: values.linkPicUrl
          };
          
          if (isEdit) {
            response = await api.put(`/materials/${id}`, submitData);
          } else {
            response = await api.post('/materials/link', submitData);
          }
          break;

        case 'MINIPROGRAM':
          // 创建小程序素材时必须有封面图
          if (!isEdit && (coverFileList.length === 0 || !coverFileList.some(file => file.originFileObj))) {
            message.error('创建小程序素材时必须选择封面图，否则发送会失败');
            setLoading(false);
            return;
          }
          
          submitData.miniprogramInfo = {
            title: values.miniprogramTitle,
            appid: values.miniprogramAppid,
            page: values.miniprogramPage,
            description: values.miniprogramDescription
          };
          
          // 无论创建还是编辑，都使用FormData支持封面图上传
          const miniprogramFormData = new FormData();
          miniprogramFormData.append('material', new Blob([JSON.stringify(submitData)], {type: 'application/json'}));
          
          // 如果有新上传的封面图
          console.log('小程序提交 - coverFileList:', coverFileList);
          if (coverFileList.length > 0 && coverFileList[0].originFileObj) {
            console.log('添加封面图到FormData:', coverFileList[0].originFileObj);
            miniprogramFormData.append('coverFile', coverFileList[0].originFileObj);
          } else {
            console.log('没有封面图文件:', coverFileList.length, coverFileList[0]?.originFileObj);
          }
          
          if (isEdit) {
            // 编辑模式：使用支持文件上传的更新接口
            response = await api.put(`/materials/${id}/miniprogram`, miniprogramFormData, {
              headers: {
                'Content-Type': undefined  // 让浏览器自动设置multipart/form-data
              }
            });
          } else {
            // 创建模式
            response = await api.post('/materials/miniprogram', miniprogramFormData, {
              headers: {
                'Content-Type': undefined  // 让浏览器自动设置multipart/form-data
              }
            });
          }
          break;
      }

      if (response?.data?.success) {
        message.success(isEdit ? '更新成功' : '创建成功');
        navigate('/materials');
      } else {
        message.error(response?.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      
      // 处理不同类型的错误，提供更具体的错误信息
      let errorMsg = '操作失败';
      
      if (error.response?.status === 413 || 
          error.message?.includes('Request Entity Too Large') ||
          error.message?.includes('Maximum upload size exceeded') ||
          error.message?.includes('FileSizeLimitExceededException')) {
        // 文件大小超限错误
        errorMsg = '文件上传失败：文件大小超过限制';
        
                 // 根据素材类型提供具体的限制说明
         if (materialType === 'IMAGE') {
           errorMsg += '\n• 图片大小不能超过10MB\n• 支持JPG、PNG格式\n• 请压缩图片后重试';
         } else if (materialType === 'VIDEO') {
           errorMsg += '\n• 视频大小不能超过10MB\n• 支持MP4格式\n• 请压缩视频后重试';
         } else if (materialType === 'MINIPROGRAM') {
           errorMsg += '\n• 小程序封面图片大小不能超过10MB\n• 支持JPG、PNG格式';
         }
      } else if (error.response?.status === 400) {
        // 业务逻辑错误
        errorMsg = error.response?.data?.message || '请求参数错误';
      } else if (error.response?.status === 500) {
        // 服务器错误
        const serverMessage = error.response?.data?.message;
        if (serverMessage?.includes('企业微信')) {
          errorMsg = '企业微信接口调用失败：' + serverMessage;
        } else {
          errorMsg = '服务器处理失败：' + (serverMessage || '请稍后重试');
        }
      } else if (error.message?.includes('Network Error')) {
        // 网络错误
        errorMsg = '网络连接失败，请检查网络后重试';
      } else if (error.response?.data?.message) {
        // 其他已知错误
        errorMsg = error.response.data.message;
      } else {
        // 未知错误
        errorMsg = `操作失败：${error.message || '未知错误'}`;
      }
      
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // 返回按钮
  const handleBack = () => {
    navigate('/materials');
  };

  // 渲染不同类型的表单内容
  const renderContentForm = () => {
    switch (materialType) {
      case 'TEXT':
        return (
          <Form.Item
            name="textContent"
            label="文字内容"
            rules={[{ required: true, message: '请输入文字内容' }]}
          >
            <TextArea 
              rows={6} 
              placeholder="请输入文字内容..."
              showCount
              maxLength={2000}
              style={{ width: '50%' }}
            />
          </Form.Item>
        );

      case 'IMAGE':
        return (
          <Form.Item
            label="图片文件"
            required={!isEdit}
            extra={isEdit ? "支持JPG、PNG格式，文件大小不超过10MB" : "支持JPG、PNG格式，文件大小不超过10MB，可同时选择多张图片批量创建，将自动使用文件名作为素材名称"}
          >
            <Upload
              {...uploadProps}
              accept="image/*"
              multiple={!isEdit}
              maxCount={isEdit ? 1 : undefined}
              listType="picture-card"
            >
              {(isEdit ? fileList.length < 1 : true) && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>{'选择图片'}</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        );

      case 'VIDEO':
        return (
          <Form.Item
            label="视频文件"
            required={!isEdit}
            extra="支持MP4格式，文件大小不超过10MB"
          >
            <Upload
              {...uploadProps}
              accept="video/*"
              maxCount={1}
              listType="picture-card"
              itemRender={(originNode, file, fileList, actions) => {
                // 自定义视频文件渲染
                const videoUrl = file.url || file.thumbUrl;
                return (
                  <div style={{ 
                    position: 'relative', 
                    width: '104px', 
                    height: '104px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    background: '#f5f5f5',
                    cursor: videoUrl ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (!videoUrl) return;
                    setPreviewModal({ open: true, src: videoUrl, type: 'video', title: file.name || '视频预览' });
                  }}>
                    {videoUrl ? (
                      <>
                        {/* 显示视频缩略图 */}
                        <video
                          src={videoUrl}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover' 
                          }}
                          muted
                          controls={false}
                        />
                        {/* 播放图标覆盖层 */}
                        <div style={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          right: 0, 
                          bottom: 0, 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          background: 'rgba(0,0,0,0.3)'
                        }}>
                          <PlayCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
                        </div>
                      </>
                    ) : (
                      // 新上传的文件，只显示图标
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                      }}>
                        <VideoCameraOutlined style={{ fontSize: '24px', color: '#666', marginBottom: '8px' }} />
                        <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', padding: '0 4px' }}>
                          {file.name}
                        </div>
                      </div>
                    )}
                    
                    {/* 删除按钮 */}
                    <div style={{ 
                      position: 'absolute', 
                      top: '4px', 
                      right: '4px',
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => { e.stopPropagation(); actions.remove(); }}>
                      <DeleteOutlined style={{ fontSize: '12px', color: 'white' }} />
                    </div>
                  </div>
                );
              }}
            >
              {fileList.length < 1 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>选择视频</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        );

      case 'LINK':
        return (
          <>
            <Form.Item
              name="linkTitle"
              label="图文消息标题"
              rules={[
                { required: true, message: '请输入图文消息标题' },
                { max: 128, message: '标题最长为128字节' }
              ]}
            >
              <Input placeholder="请输入图文消息标题..." style={{ width: '50%' }} />
            </Form.Item>
            
            <Form.Item
              name="linkUrl"
              label="图文消息链接"
              rules={[
                { required: true, message: '请输入图文消息链接' },
                { type: 'url', message: '请输入有效的URL地址' }
              ]}
            >
              <Input placeholder="https://..." style={{ width: '50%' }} />
            </Form.Item>

            {/* 隐藏字段：存储封面URL */}
            <Form.Item
              name="linkPicUrl"
              hidden
            >
              <Input />
            </Form.Item>

            {/* 隐藏字段：存储封面media_id */}
            <Form.Item
              name="linkPicMediaId"
              hidden
            >
              <Input />
            </Form.Item>

            {/* 封面图片选择 */}
            <Form.Item
              label="图文消息封面"
              required
              rules={[{ required: true, message: '请选择封面图片' }]}
            >
              {selectedCoverMaterial || previewUrls.linkPreview ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                  <Image
                    src={previewUrls.linkPreview}
                    width={120}
                    height={120}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, marginBottom: 4 }}>
                      {selectedCoverMaterial?.name || '已选择封面图'}
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                      {previewUrls.linkPreview}
                    </Typography.Text>
                  </div>
                  <Button
                    size="small"
                    onClick={() => setCoverSelectorVisible(true)}
                  >
                    重新选择
                  </Button>
                </div>
              ) : (
                <Button
                  type="dashed"
                  icon={<FileImageOutlined />}
                  onClick={() => setCoverSelectorVisible(true)}
                  block
                  style={{ height: 100, fontSize: 14 }}
                >
                  从素材库选择封面图片（必选）
                </Button>
              )}
            </Form.Item>

            <Form.Item
              name="linkDescription"
              label="图文消息描述（可选）"
              rules={[{ max: 512, message: '描述最长为512字节' }]}
            >
              <TextArea rows={3} placeholder="请输入图文消息描述..." style={{ width: '50%' }} />
            </Form.Item>
          </>
        );

      case 'MINIPROGRAM':
        return (
          <>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.miniprogramTitle !== cur.miniprogramTitle}>
              {({ getFieldValue }) => (
                <Form.Item
                  name="miniprogramTitle"
                  label="小程序消息标题"
                  rules={[
                    { required: true, message: '请输入小程序消息标题' },
                    { validator: validateMiniprogramTitle }
                  ]}
                  getValueFromEvent={(e) => {
                    const value = e?.target?.value || '';
                    return value.slice(0, 20);
                  }}
                >
                  <Input 
                    placeholder="请输入小程序消息标题..." 
                    style={{ width: '50%' }} 
                    showCount
                    maxLength={20}
                  />
                </Form.Item>
              )}
            </Form.Item>
            
            <Form.Item
              name="miniprogramAppid"
              label="小程序AppID"
              rules={[{ required: true, message: '请输入小程序AppID' }]}
            >
              <Input placeholder="请输入小程序AppID..." style={{ width: '50%' }} />
            </Form.Item>
            
            <Form.Item
              name="miniprogramPage"
              label="小程序页面路径"
              rules={[{ required: true, message: '请输入小程序页面路径' }]}
            >
              <Input 
                placeholder="/pages/index" 
                style={{ width: '50%' }} 
                onChange={(e) => {
                  let value = e.target.value;
                  // 自动补全开头的斜杠
                  if (value && !value.startsWith('/')) {
                    value = '/' + value;
                  }
                  form.setFieldsValue({ miniprogramPage: value });
                }}
              />
            </Form.Item>
            
            <Form.Item
              label="小程序消息封面图片"
              required={!isEdit}
              extra={isEdit ? "封面图建议尺寸为520*416，支持JPG、PNG格式，文件大小不超过10MB" : "创建小程序素材时必须选择封面图，建议尺寸为520*416，支持JPG、PNG格式，文件大小不超过10MB"}
            >
              <Upload
                {...coverUploadProps}
                accept="image/*"
                maxCount={1}
                listType="picture-card"
              >
                {coverFileList.length < 1 && (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>选择封面</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };

  // 如果没有tenantId，显示错误页面
  if (!tenantId) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <div style={{ marginBottom: '20px' }}>
          <FileTextOutlined style={{ fontSize: '64px', color: '#ccc' }} />
        </div>
        <h3>未找到租户信息</h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          请重新登录以获取租户信息
        </p>
        <Button type="primary" onClick={() => window.location.reload()}>
          刷新页面
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* 页面头部 */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
            <Breadcrumb>
                <Breadcrumb.Item onClick={handleBack}>
                    <Title level={5} style={{ cursor: 'pointer', color: '#1677ff', margin: 0 }}>素材库</Title>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Title level={5} style={{ margin: 0 }}>{isEdit ? '编辑' : '创建'}{currentTypeConfig?.label}素材</Title>
                </Breadcrumb.Item>
            </Breadcrumb>
        </Col>
      </Row>

      {/* 表单内容 */}
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}

          initialValues={{
            materialType
          }}
        >
          {/* 基础信息 */}
          <Title level={4}>基础信息</Title>
          {/* 图片类型创建模式下不显示名称输入框 */}
          {!(materialType === 'IMAGE' && !isEdit) && (
            <Form.Item
              name="name"
              label="素材名称"
              rules={[{ required: true, message: '请输入素材名称' }]}
            >
              <Input 
                placeholder="请输入素材名称..." 
                style={{ width: '50%' }} 
              />
            </Form.Item>
          )}
          
          <Form.Item
            name="groupId"
            label="所属分组"
          >
            <TreeSelect
              placeholder="请选择分组（可选）"
              allowClear
              showSearch
              treeNodeFilterProp="title"
              filterTreeNode={(inputValue, treeNode) => {
                // 支持拼音和中文搜索
                const title = treeNode.title ? treeNode.title.toString().toLowerCase() : '';
                const input = inputValue.toLowerCase();
                return title.includes(input);
              }}
              treeData={groupTree}
              treeDefaultExpandAll
              style={{ width: '50%' }}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="素材描述"
          >
            <TextArea 
              rows={3} 
              placeholder="请输入素材描述，便于后续查找和使用..."
              showCount
              maxLength={500}
              style={{ width: '50%' }}
            />
          </Form.Item>

          <Divider />

          {/* 素材内容 */}
          <Title level={4}>
            {currentTypeConfig?.label}内容
            <Text type="secondary" style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 'normal' }}>
              根据素材类型填写相应内容
            </Text>
          </Title>

          {renderContentForm()}

        </Form>
      </Card>

      {/* 预览弹窗（图片/视频） */}
      <Modal
        centered
        open={previewModal.open}
        title={previewModal.title}
        footer={null}
        onCancel={() => setPreviewModal(prev => ({ ...prev, open: false }))}
        width={previewModal.type === 'video' ? '70vw' : '60vw'}
        style={{ maxWidth: previewModal.type === 'video' ? 900 : 720 }}
      >
        {previewModal.type === 'video' ? (
          <video
            src={previewModal.src}
            style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', backgroundColor: '#000' }}
            controls
            autoPlay
          />
        ) : (
          <img
            alt={previewModal.title}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block', margin: '0 auto' }}
            src={previewModal.src}
          />
        )}
      </Modal>

      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      {/* 固定底部按钮栏 */}
      <div style={fixedFooterStyle}>
        <Space>
          <Button size="middle" onClick={handleBack}>
            取消
          </Button>
          <Button
            type="primary"
            loading={loading}
            icon={<SaveOutlined />}
            size="middle"
            onClick={() => form.submit()}
          >
            {isEdit ? '保存修改' : '创建素材'}
          </Button>
        </Space>
      </div>

      {/* 封面素材选择器 */}
      <MaterialSelector
        visible={coverSelectorVisible}
        onCancel={() => setCoverSelectorVisible(false)}
        onSelect={handleSelectCoverMaterial}
        materialType="IMAGE"
        multiSelect={false}
        title="选择链接封面图片"
        tenantId={tenantId}
      />
    </div>
  );
};

export default MaterialEdit; 
